import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { AuthGateway } from '../application/AuthGateway'
import { AuthFailure, type OperatorSession } from '../domain/session'
import { AuthContext, type AuthState } from './AuthContext'

function message(error: unknown): string {
  return error instanceof AuthFailure ? error.message : 'Une erreur inattendue est survenue. Réessayez.'
}

export function AuthProvider({ gateway, children }: { gateway: AuthGateway; children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading')
  const [session, setSession] = useState<OperatorSession | null>(null)
  const [error, setError] = useState<string | null>(null)

  const apply = useCallback((next: OperatorSession | null) => {
    setSession(next)
    setStatus(next ? 'signed-in' : 'signed-out')
  }, [])

  const refresh = useCallback(async () => {
    try {
      apply(await gateway.getSession())
    } catch (failure) {
      setError(message(failure))
      apply(null)
    }
  }, [gateway, apply])

  useEffect(() => {
    let cancelled = false
    gateway.getSession().then(
      (next) => {
        if (!cancelled) apply(next)
      },
      (failure: unknown) => {
        if (cancelled) return
        setError(message(failure))
        apply(null)
      },
    )
    return () => {
      cancelled = true
    }
  }, [gateway, apply])

  const value = useMemo<AuthState>(
    () => ({
      status,
      session,
      error,
      refresh,
      async signIn(identifier, password) {
        setError(null)
        try {
          apply(await gateway.signIn(identifier, password))
        } catch (failure) {
          setError(message(failure))
          throw failure
        }
      },
      async signOut() {
        await gateway.signOut().catch(() => undefined)
        apply(null)
      },
      async replaceTemporaryPassword(currentPassword, newPassword) {
        setError(null)
        try {
          apply(await gateway.replaceTemporaryPassword(currentPassword, newPassword))
        } catch (failure) {
          setError(message(failure))
          throw failure
        }
      },
    }),
    [status, session, error, refresh, gateway, apply],
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
